import React, { useEffect, useMemo } from 'react';
import { useParams, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import { PageBackground } from 'components/components';
import NavBar from 'modules/navbar';
import {
  RelatedUserDetailsSection,
  RelatedUserInfoSection,
  RelatedUserProfileContainer,
} from 'modules/relationships/components';
import RelatedUserInfo from 'modules/relationships/profile/components/RelatedUserInfo';
import RelatedUserChallengeDetails from 'modules/relationships/profile/components/RelatedUserChallengeDetails';
import { AppDispatch } from 'redux/typings';
import { getSubmission } from './actions';
import { selectSubmission } from './selectors';
import { RelationshipType } from 'models/Relationship.d';
import localizedStrings from 'localization';
import useActionStatus from 'hooks/useActionStatus';
import LoadingScreenSimple from 'modules/loading-screen-simple';

const ExternalFeedback = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const submission = useSelector(selectSubmission);
  const [isPending] = useActionStatus(getSubmission);

  const { token, appletId } = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return {
      token: urlParams.get('authToken'),
      appletId: urlParams.get('appletId'),
    };
  }, [search]);

  const filteredSubmission = useMemo(() => {
    if (!submission) return undefined;

    if (!appletId || !submission.applets) return submission;

    const filteredApplets = submission.applets.filter((applet) => applet.id === appletId);

    return {
      ...submission,
      applets: filteredApplets,
    };
  }, [submission, appletId]);

  const relationshipType = useMemo(
    () =>
      submission?.relationshipType === RelationshipType.EXTERNAL_MENTOR
        ? localizedStrings.ngaParticipant
        : RelationshipType.RUNNING_PARTNER,
    [submission],
  );

  useEffect(() => {
    if (id && token) {
      dispatch(getSubmission({ id, token }));
    }
  }, [dispatch, id, token]);

  if (isPending) {
    return <LoadingScreenSimple />
  }

  return (
    <>
      <NavBar hideControls />

      <PageBackground>
        <RelatedUserProfileContainer>
          <RelatedUserInfoSection>
            <RelatedUserInfo
              displayName={submission?.authorDisplayName ?? ''}
              profilePictureUrl={submission?.authorProfilePictureUrl}
              relationshipType={relationshipType}
              hideChat
            />
          </RelatedUserInfoSection>
          <RelatedUserDetailsSection>
            <RelatedUserChallengeDetails
              submission={filteredSubmission}
              external
              token={token}
            />
          </RelatedUserDetailsSection>
        </RelatedUserProfileContainer>
      </PageBackground>
    </>
  );
};

export default ExternalFeedback;
